/**
 * Circuit preview for the workshop: the whole layout scaled into a canvas,
 * surface colors, start line, and the (in-game invisible) checkpoint gates as
 * an authoring aid.
 */

import type { Track, TrackSample } from '@microligue/shared';
import { computeCheckpoints } from '@microligue/shared';
import { SURFACE_COLORS } from './surfaces';

const PAD = 14;

export function drawTrackPreview(canvas: HTMLCanvasElement, track: Track): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  // Crisp rendering: match the backing store to the CSS size × devicePixelRatio.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = canvas.clientWidth || canvas.width;
  const cssH = canvas.clientHeight || canvas.height;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  const w = canvas.width;
  const h = canvas.height;
  const b = track.bounds;
  const scale = Math.min((w - PAD * 2) / (b.maxX - b.minX), (h - PAD * 2) / (b.maxY - b.minY));
  const ox = PAD + (w - PAD * 2 - (b.maxX - b.minX) * scale) / 2 - b.minX * scale;
  const oy = PAD + (h - PAD * 2 - (b.maxY - b.minY) * scale) / 2 - b.minY * scale;
  const px = (x: number): number => x * scale + ox;
  const py = (y: number): number => y * scale + oy;

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);

  // Band drawn as thick round-capped strokes, one polyline per surface run.
  const samples = track.samples;
  const n = samples.length;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (let i = 0; i < n; i++) {
    const a = samples[i] as TrackSample;
    const c = samples[(i + 1) % n] as TrackSample;
    ctx.strokeStyle = SURFACE_COLORS[a.surface];
    ctx.lineWidth = Math.max(2, a.halfWidth * 2 * scale);
    ctx.beginPath();
    ctx.moveTo(px(a.x), py(a.y));
    ctx.lineTo(px(c.x), py(c.y));
    ctx.stroke();
  }

  // Checkpoint gates (authoring aid — invisible in game).
  ctx.strokeStyle = 'rgba(255, 176, 46, 0.8)';
  ctx.lineWidth = 2;
  for (const s of computeCheckpoints(track)) {
    const idx = Math.round(s / track.step) % n;
    const smp = samples[idx] as TrackSample;
    const nx = -smp.ty;
    const ny = smp.tx;
    ctx.beginPath();
    ctx.moveTo(px(smp.x - nx * smp.halfWidth), py(smp.y - ny * smp.halfWidth));
    ctx.lineTo(px(smp.x + nx * smp.halfWidth), py(smp.y + ny * smp.halfWidth));
    ctx.stroke();
  }

  // Start line.
  const s0 = samples[0] as TrackSample;
  const nx = -s0.ty;
  const ny = s0.tx;
  ctx.strokeStyle = '#f7f3e8';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(px(s0.x - nx * s0.halfWidth), py(s0.y - ny * s0.halfWidth));
  ctx.lineTo(px(s0.x + nx * s0.halfWidth), py(s0.y + ny * s0.halfWidth));
  ctx.stroke();
}
